import axios from 'axios';
import { createApiUrl } from './apiConfig';

const MAX_ASSET_FILE_BYTES = 8 * 1024 * 1024 * 1024;

function formatMaxSizeLabel() {
  return `${MAX_ASSET_FILE_BYTES / (1024 ** 3)}GB`;
}

async function uploadFileSinglePut(file, presign, fileStart, totalBytes, onProgress) {
  await axios.put(presign.uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: (evt) => {
      if (!onProgress || !evt.total) return;
      const overall = fileStart + evt.loaded;
      onProgress(Math.min(100, Math.round((overall / totalBytes) * 100)));
    },
  });
}

async function uploadFileMultipart(file, presign, fileStart, totalBytes, onProgress) {
  const { uploadId, key, partSize, parts } = presign;
  const completedParts = [];
  let partOffset = 0;

  for (const part of parts) {
    const start = (part.partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const res = await axios.put(part.uploadUrl, blob, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      timeout: 0,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });

    let etag = res.headers?.etag || res.headers?.ETag;
    if (typeof etag === 'string') {
      etag = etag.replace(/^"|"$/g, '');
    }
    completedParts.push({ PartNumber: part.partNumber, ETag: etag });

    partOffset += blob.size;
    if (onProgress) {
      const overall = fileStart + partOffset;
      onProgress(Math.min(100, Math.round((overall / totalBytes) * 100)));
    }
  }

  await axios.post(
    createApiUrl('/upload-asset/multipart-complete'),
    { uploadId, key, parts: completedParts },
    { withCredentials: true }
  );
}

/** DragDropFileUpload always passes an array; normalize to a single File. */
function normalizeSingleFile(fileOrArray) {
  if (!fileOrArray) return null;
  if (Array.isArray(fileOrArray)) return fileOrArray[0] || null;
  return fileOrArray;
}

function normalizeFileList(files) {
  if (!files) return [];
  return Array.isArray(files) ? files : [files];
}

export function hasAssetFilesToUpload({ video, thumbnail, documents, resources }) {
  return Boolean(
    normalizeSingleFile(video) ||
    normalizeSingleFile(thumbnail) ||
    normalizeFileList(documents).length ||
    normalizeFileList(resources).length
  );
}

export { normalizeSingleFile, normalizeFileList };

/**
 * Request presigned URLs, upload files directly to S3, return metadata for /complete.
 */
export async function uploadAssetFilesToS3({ video, thumbnail, documents, resources, onProgress }) {
  const fileEntries = [];

  const videoFile = normalizeSingleFile(video);
  const thumbnailFile = normalizeSingleFile(thumbnail);
  const documentFiles = normalizeFileList(documents);
  const resourceFiles = normalizeFileList(resources);

  if (videoFile) fileEntries.push({ field: 'video', file: videoFile });
  if (thumbnailFile) fileEntries.push({ field: 'thumbnail', file: thumbnailFile });
  for (const file of documentFiles) {
    fileEntries.push({ field: 'documents', file });
  }
  for (const file of resourceFiles) {
    fileEntries.push({ field: 'resources', file });
  }

  if (fileEntries.length === 0) {
    return { video: null, thumbnail: null, documents: [], resources: [] };
  }

  for (const { file } of fileEntries) {
    if (file.size > MAX_ASSET_FILE_BYTES) {
      throw new Error(`"${file.name}" exceeds the ${formatMaxSizeLabel()} limit`);
    }
  }

  const { data: presignData } = await axios.post(
    createApiUrl('/upload-asset/presign'),
    {
      files: fileEntries.map(({ field, file }) => ({
        field,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })),
    },
    { withCredentials: true }
  );

  const uploads = presignData.uploads || [];
  if (uploads.length !== fileEntries.length) {
    throw new Error('Upload preparation failed');
  }

  const result = { video: null, thumbnail: null, documents: [], resources: [] };
  const totalBytes = fileEntries.reduce((sum, e) => sum + e.file.size, 0);
  let uploadedBytes = 0;

  for (let i = 0; i < fileEntries.length; i++) {
    const { field, file } = fileEntries[i];
    const presign = uploads[i];
    const fileStart = uploadedBytes;

    if (presign.multipart) {
      await uploadFileMultipart(file, presign, fileStart, totalBytes, onProgress);
    } else {
      await uploadFileSinglePut(file, presign, fileStart, totalBytes, onProgress);
    }

    uploadedBytes += file.size;
    const meta = { key: presign.key, url: presign.url, name: file.name, filename: file.name };

    if (field === 'video') result.video = meta;
    else if (field === 'thumbnail') result.thumbnail = meta;
    else if (field === 'documents') result.documents.push(meta);
    else if (field === 'resources') result.resources.push(meta);
  }

  if (onProgress) onProgress(100);
  return result;
}

/** @deprecated use MAX_ASSET_FILE_BYTES */
const FIVE_GB = MAX_ASSET_FILE_BYTES;

export { MAX_ASSET_FILE_BYTES, FIVE_GB };
